import { ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserCapabilities } from "@/hooks/useUserCapabilities";

interface OwnerGuardProps {
  children: ReactNode;
}

/**
 * Host workspace: own units, shared units, or starting Yours (Slice 25).
 * Dual-role (also a cleaner) may stay here. Cleaner-only can open Properties to add their own.
 */
const OwnerGuard = ({ children }: OwnerGuardProps) => {
  const { user, profile, loading } = useAuth();
  const { is_owner, is_cohost, is_cleaner, can_create_properties, isLoading: capsLoading } =
    useUserCapabilities();
  const navigate = useNavigate();

  const canAccessOwnerRoutes =
    is_owner || is_cohost || can_create_properties || profile?.role === "owner";
  const needsEmailVerify = Boolean(profile && !profile.email_verified && profile.role !== "cleaner");

  useEffect(() => {
    if (loading || capsLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    if (!profile) return;
    if (!canAccessOwnerRoutes) {
      if (is_cleaner) {
        navigate("/worker/schedule", { replace: true });
        return;
      }
      if (profile.role === "owner") {
        navigate("/", { replace: true });
        return;
      }
      navigate("/login");
      return;
    }
    if (needsEmailVerify) {
      navigate("/verify-email");
    }
  }, [user, profile, loading, capsLoading, canAccessOwnerRoutes, is_cleaner, needsEmailVerify, navigate]);

  if (loading || capsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p>Loading...</p>
      </div>
    );
  }

  if (!user || !profile || !canAccessOwnerRoutes) return null;
  if (needsEmailVerify) return null;

  return <>{children}</>;
};

export default OwnerGuard;
